import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import type { EventFilters, FilterPanelState, SortField, SortOrder } from "@/types";
import { DEFAULT_FILTERS } from "@/types";

function appendTristate(out: EventFilters, key: keyof EventFilters, value: boolean | null) {
  if (value !== null) {
    (out as Record<string, unknown>)[key] = value ? "with" : "without";
  }
}

function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildApiFilters(f: FilterPanelState): EventFilters {
  const out: EventFilters = {};
  if (f.startDate) out.start_date = formatLocalDate(f.startDate);
  if (f.endDate) out.end_date = formatLocalDate(f.endDate);
  if (f.minScore > 0) out.min_score = f.minScore;
  if (f.maxScore < 100) out.max_score = f.maxScore;
  if (f.countryCodes.length > 0) out.country_codes = f.countryCodes;
  if (f.asns.length > 0) out.asns = f.asns;
  if (f.ipSearch) out.ip_search = f.ipSearch;
  appendTristate(out, "has_blocked", f.hasBlockedAttempts);
  appendTristate(out, "has_failures", f.hasFailures);
  appendTristate(out, "has_successful_auth", f.hasSuccessfulAuth);
  appendTristate(out, "has_user_not_found", f.hasUserNotFound);
  appendTristate(out, "has_invalid_password", f.hasInvalidPassword);
  appendTristate(out, "has_failed_second_factor", f.hasFailedSecondFactor);
  return out;
}

const PANEL_KEY_MAP: Record<string, keyof FilterPanelState> = {
  country_codes: "countryCodes",
  asns: "asns",
};

interface RemoveArrayOpts {
  next: EventFilters;
  key: keyof EventFilters;
  value: string;
}

function removeArrayFilter(
  { next, key, value }: RemoveArrayOpts,
  setPanelFilters: React.Dispatch<React.SetStateAction<FilterPanelState>>,
) {
  const arr = (next[key] as string[]).filter((v) => v !== value);
  if (arr.length > 0) {
    (next as Record<string, unknown>)[key] = arr;
  } else {
    delete (next as Record<string, unknown>)[key];
  }
  const panelKey = PANEL_KEY_MAP[key];
  if (panelKey) {
    setPanelFilters((p) => ({
      ...p,
      [panelKey]: (p[panelKey] as string[]).filter((v) => v !== value),
    }));
  }
}

const TRISTATE_RESET_MAP: Record<string, Partial<FilterPanelState>> = {
  has_blocked: { hasBlockedAttempts: null },
  has_successful_auth: { hasSuccessfulAuth: null },
  has_user_not_found: { hasUserNotFound: null },
  has_invalid_password: { hasInvalidPassword: null },
  has_failed_second_factor: { hasFailedSecondFactor: null },
};

function removeScalarFilter(
  next: EventFilters,
  key: keyof EventFilters,
  setPanelFilters: React.Dispatch<React.SetStateAction<FilterPanelState>>,
) {
  if (key === "min_score" || key === "max_score") {
    delete next.min_score;
    delete next.max_score;
    setPanelFilters((p) => ({ ...p, minScore: 0, maxScore: 100 }));
  } else if (key === "start_date" || key === "end_date") {
    delete next.start_date;
    delete next.end_date;
    setPanelFilters((p) => ({ ...p, startDate: null, endDate: null }));
  } else if (key === "ip_search") {
    delete next.ip_search;
    setPanelFilters((p) => ({ ...p, ipSearch: "" }));
  } else if (key === "has_failures") {
    delete next.has_failures;
    delete next.has_user_not_found;
    delete next.has_invalid_password;
    delete next.has_failed_second_factor;
    setPanelFilters((p) => ({
      ...p,
      hasFailures: null,
      hasUserNotFound: null,
      hasInvalidPassword: null,
      hasFailedSecondFactor: null,
    }));
  } else if (key in TRISTATE_RESET_MAP) {
    delete (next as Record<string, unknown>)[key];
    setPanelFilters((p) => ({ ...p, ...TRISTATE_RESET_MAP[key] }));
  } else {
    delete (next as Record<string, unknown>)[key];
  }
}

function parseTristateParam(params: URLSearchParams, key: string): boolean | null {
  const v = params.get(key);
  if (v === "with") return true;
  if (v === "without") return false;
  return null;
}

function parseFiltersFromParams(params: URLSearchParams): FilterPanelState | null {
  if (params.size === 0) return null;
  const startStr = params.get("start_date");
  const endStr = params.get("end_date");
  const minStr = params.get("min_score");
  const maxStr = params.get("max_score");
  return {
    startDate: startStr ? new Date(startStr + "T00:00:00") : null,
    endDate: endStr ? new Date(endStr + "T00:00:00") : null,
    minScore: minStr ? Number(minStr) : 0,
    maxScore: maxStr ? Number(maxStr) : 100,
    countryCodes: params.getAll("country_codes"),
    asns: params.getAll("asns"),
    ipSearch: params.get("ip_search") ?? "",
    hasBlockedAttempts: parseTristateParam(params, "has_blocked"),
    hasFailures: parseTristateParam(params, "has_failures"),
    hasSuccessfulAuth: parseTristateParam(params, "has_successful_auth"),
    hasUserNotFound: parseTristateParam(params, "has_user_not_found"),
    hasInvalidPassword: parseTristateParam(params, "has_invalid_password"),
    hasFailedSecondFactor: parseTristateParam(params, "has_failed_second_factor"),
  };
}

function filtersToParams(filters: EventFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.start_date) params.set("start_date", filters.start_date);
  if (filters.end_date) params.set("end_date", filters.end_date);
  if (filters.min_score !== undefined) params.set("min_score", String(filters.min_score));
  if (filters.max_score !== undefined) params.set("max_score", String(filters.max_score));
  for (const c of filters.country_codes ?? []) params.append("country_codes", c);
  for (const a of filters.asns ?? []) params.append("asns", a);
  if (filters.ip_search) params.set("ip_search", filters.ip_search);
  appendTristateParams(params, filters);
  return params;
}

function appendTristateParams(params: URLSearchParams, filters: EventFilters) {
  const keys = [
    "has_blocked", "has_failures", "has_successful_auth",
    "has_user_not_found", "has_invalid_password", "has_failed_second_factor",
  ] as const;
  for (const key of keys) {
    if (filters[key]) params.set(key, filters[key]);
  }
}

// Route that owns these filters; the URL sync only writes while we are here.
const EVENTS_PATH = "/ip-activity";

const VALID_SORT_FIELDS = new Set<SortField>(["fraud_score", "last_activity", "blocked_recent", "failures_recent", "country_code"]);

function parseSortFromParams(params: URLSearchParams): { sortBy: SortField; sortOrder: SortOrder } {
  const field = params.get("sort_by") as SortField | null;
  const order = params.get("sort_order") as SortOrder | null;
  return {
    sortBy: field && VALID_SORT_FIELDS.has(field) ? field : "fraud_score",
    sortOrder: order === "asc" || order === "desc" ? order : "desc",
  };
}

function toggleSort(
  field: SortField,
  current: { sortBy: SortField },
  setters: { setSortBy: React.Dispatch<React.SetStateAction<SortField>>; setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>; setPage: React.Dispatch<React.SetStateAction<number>> },
) {
  if (field === current.sortBy) {
    setters.setSortOrder((o) => (o === "desc" ? "asc" : "desc"));
  } else {
    setters.setSortBy(field);
    setters.setSortOrder("desc");
  }
  setters.setPage(0);
}

export function useEventFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [panelFilters, setPanelFilters] = useState<FilterPanelState>(() => parseFiltersFromParams(searchParams) ?? DEFAULT_FILTERS);
  const [apiFilters, setApiFilters] = useState<EventFilters>(() => {
    const parsed = parseFiltersFromParams(searchParams);
    return parsed ? buildApiFilters(parsed) : {};
  });
  const [page, setPage] = useState(0);
  const initialSort = parseSortFromParams(searchParams);
  const [sortBy, setSortBy] = useState<SortField>(initialSort.sortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSort.sortOrder);

  useEffect(() => {
    // Guard against clobbering an in-flight navigation. `setSearchParams` is
    // recreated on every location change, so this effect can still be pending
    // when the user navigates away (e.g. a fast click to another page before
    // this one settles). A pending effect holds the old in-closure location, so
    // we check the *live* path: if we're no longer on the events route, skip the
    // write rather than bounce the browser back to /ip-activity.
    if (typeof window !== "undefined" && window.location.pathname !== EVENTS_PATH) return;
    const params = filtersToParams(apiFilters);
    params.set("sort_by", sortBy);
    params.set("sort_order", sortOrder);
    setSearchParams(params, { replace: true });
  }, [apiFilters, sortBy, sortOrder, setSearchParams]);

  // Sync externally-set ip_search (e.g. top-nav search while already on /events).
  // Using the setState-during-render pattern to adjust state when the URL
  // param changes without cascading renders from an effect.
  const urlIpSearch = searchParams.get("ip_search") ?? "";
  const [lastSyncedIp, setLastSyncedIp] = useState(urlIpSearch);
  if (urlIpSearch !== lastSyncedIp) {
    setLastSyncedIp(urlIpSearch);
    setApiFilters((f) => {
      const next = { ...f };
      if (urlIpSearch) next.ip_search = urlIpSearch;
      else delete next.ip_search;
      return next;
    });
    setPanelFilters((p) => ({ ...p, ipSearch: urlIpSearch }));
    setPage(0);
  }

  const handleSort = useCallback((field: SortField) => toggleSort(field, { sortBy }, { setSortBy, setSortOrder, setPage }), [sortBy]);
  const handleApply = useCallback(() => { setApiFilters(buildApiFilters(panelFilters)); setPage(0); }, [panelFilters]);
  const handleReset = useCallback(() => { setPanelFilters(DEFAULT_FILTERS); setApiFilters({}); setPage(0); }, []);

  const handleRemoveFilter = useCallback(
    (key: keyof EventFilters, value?: string) => {
      const next = { ...apiFilters };
      if (value && Array.isArray(next[key])) removeArrayFilter({ next, key, value }, setPanelFilters);
      else removeScalarFilter(next, key, setPanelFilters);
      setApiFilters(next);
    },
    [apiFilters],
  );

  return {
    panelFilters, setPanelFilters, apiFilters: { ...apiFilters, sort_by: sortBy, sort_order: sortOrder } as EventFilters,
    sortBy, sortOrder, handleSort, page, setPage, handleApply, handleReset, handleRemoveFilter,
  };
}
